import { createElement as _createElement } from "@emotion/react";
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Button, Dropdown, Margin, Text, } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { Fragment, useState } from 'react';
import { MEDIA_MIN_WIDTH } from '../../constants/responsive';
import { useTracking } from '../../tracking';
import { useScrollHeight } from '../../utils/useScrollHeight';
import { Group } from './Group';
const styles = labelStyles('ComparisonTable', {
    body: css({
        maxWidth: '1240px',
        padding: '0 20px',
        margin: 'auto',
    }),
    mobileDropdownContainer: css({
        display: 'grid',
        gridTemplateColumns: '50% 50%',
        gap: '1px',
        visibility: 'visible',
        [MEDIA_MIN_WIDTH.small]: {
            display: 'none',
            visibility: 'hidden',
        },
    }),
    collapsedGroups: css({
        maxHeight: 0,
        overflow: 'hidden',
        transitionProperty: 'all',
        transitionTimingFunction: 'linear',
    }),
    collapsedGroupsInnerContainer: css({
        transition: 'all 250ms linear',
    }),
    expansionButtonContainer: css({
        display: 'flex',
        justifyContent: 'center',
        padding: '50px 0',
    }),
    tagLabelWrapper: css({
        marginLeft: '5px',
    }),
});
export function ComparisonTable({ columns = [], groups = [], hasExpansionButton, leftColumnMinWidth, expansionButtonConfig, title, }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [leftColumnIndex, setLeftColumnIndex] = useState(Math.max(columns.findIndex((col) => col.isDefaultLeftColumn), 0));
    const [rightColumnIndex, setRightColumnIndex] = useState(Math.max(columns.findIndex((col) => col.isDefaultRightColumn), 0));
    const [collapsedGroupsRef, expandedHeight] = useScrollHeight();
    const { groupsOpenByDefault } = expansionButtonConfig || {};
    const defaultGroups = hasExpansionButton
        ? groups.slice(0, groupsOpenByDefault)
        : groups;
    const collapsedGroups = groups.slice(groupsOpenByDefault);
    const columnHeaders = columns.map((col) => col.header);
    const leftColumnHeader = columnHeaders[leftColumnIndex];
    const rightColumnHeader = columnHeaders[rightColumnIndex];
    const leftColumnDropdown = (_jsx(Dropdown, { menuAriaLabel: "Left column plan", placeholder: "Select a plan", items: columnHeaders.map((label, value) => ({ label, value })), selectedItem: {
            label: leftColumnHeader,
            value: leftColumnIndex,
        }, onChange: (item) => setLeftColumnIndex(item.value), isFullWidth: true }));
    const rightColumnDropdown = (_jsx(Dropdown, { menuAriaLabel: "Right column plan", placeholder: "Select a plan", getLabelForItem: (item) => item.label, items: columnHeaders.map((label, value) => ({ label, value })), selectedItem: {
            label: rightColumnHeader,
            value: rightColumnIndex,
        }, onChange: (item) => setRightColumnIndex(item.value), isFullWidth: true }));
    const groupProps = {
        columnHeaders,
        leftColumnDropdown,
        leftColumnHeader,
        leftColumnIndex,
        leftColumnMinWidth,
        rightColumnDropdown,
        rightColumnHeader,
        rightColumnIndex,
    };
    return (_jsxs("div", Object.assign({ css: styles.body }, { children: [title && (_jsx(Margin, Object.assign({ margin: "10px 0" }, { children: _jsx(Text, Object.assign({ tag: "h2", type: "paragraph1ShortBold" }, { children: title })) }))), _jsxs("div", Object.assign({ css: styles.mobileDropdownContainer }, { children: [leftColumnDropdown, rightColumnDropdown] })), _jsx("div", { children: defaultGroups.map((group, index) => (_createElement(Group, Object.assign({}, groupProps, { key: group.title, title: group.title, rows: group.rows, isFirstGroup: index === 0 })))) }), hasExpansionButton &&
                expansionButtonConfig &&
                collapsedGroups.length > 0 && (_jsxs(Fragment, { children: [_jsx(ExpansionButtons, Object.assign({ onClick: setIsExpanded, isExpanded: isExpanded }, expansionButtonConfig)), _jsx("div", Object.assign({ ref: collapsedGroupsRef, css: styles.collapsedGroups, style: {
                            maxHeight: isExpanded ? expandedHeight : 0,
                            visibility: isExpanded ? 'visible' : 'hidden',
                            transitionDuration: `${expandedHeight * 0.1}ms`,
                        } }, { children: _jsxs("div", Object.assign({ css: styles.collapsedGroupsInnerContainer, style: {
                                opacity: isExpanded ? 1 : 0,
                                transform: isExpanded ? '' : 'translateY(-12px)',
                            } }, { children: [collapsedGroups.map((group) => (_createElement(Group, Object.assign({}, groupProps, { key: group.title, title: group.title, rows: group.rows })))), _jsx(ExpansionButtons, Object.assign({ onClick: setIsExpanded, isExpanded: isExpanded }, expansionButtonConfig))] })) }))] }))] })));
}
const ExpansionButtons = (props) => {
    const { emitUserInteractionEvent } = useTracking();
    if (props.isExpanded && !props.seeLessText)
        return null;
    return (_jsx("div", Object.assign({ css: styles.expansionButtonContainer }, { children: !props.isExpanded ? (_jsx(Button, Object.assign({ color: props.color, iconBefore: props === null || props === void 0 ? void 0 : props.seeMoreIcon, onClick: () => {
                emitUserInteractionEvent({
                    event_action: 'click',
                    interaction_name: 'show_click',
                    interaction_raw_text: props.seeMoreText,
                    option_selected: 'show_button',
                });
                props.onClick(true);
            } }, { children: props.seeMoreText }))) : (_jsx(Button, Object.assign({ iconBefore: props.seeLessIcon, color: props.color, onClick: () => {
                emitUserInteractionEvent({
                    event_action: 'click',
                    interaction_name: 'hide_click',
                    interaction_raw_text: props.seeLessText,
                    option_selected: 'hide_button',
                });
                props.onClick(false);
            } }, { children: props.seeLessText }))) })));
};
//# sourceMappingURL=ComparisonTable.js.map